import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { getIsExclusive, getIsLoading } from 'store/property/propertySelectors';
import { getExclusiveOffer, getIsLoading as getIsLoadingExclusiveOffers } from 'store/exclusiveOffer/exclusiveOfferSelectors';
import { Box, Hide, ImageGallery, ImageGrid } from '@qga/roo-ui/components';
import { useDispatch, useSelector } from 'react-redux';
import { setFullscreenGalleryContent } from 'store/ui/uiActions';
import noop from 'lodash/noop';
import ResultLoader from 'components/Loader/ResultLoader';
import LoaderSkeletonCard from './LoaderSkeletonCard';

const LoadedImageGallery = ({ images, onClick = noop, onImageChanged = noop }) => {
  const isExclusive = useSelector(getIsExclusive);
  const dispatch = useDispatch();
  const gridItems = isExclusive ? 3 : 4;
  const imageGridLayout = images.length < 5 ? 'full' : `${gridItems}-side`;
  const imageGalleryList = images.map(({ urlLarge, urlSmall, caption }, index) => ({
    src: urlLarge,
    alt: caption,
    thumbnail: urlSmall,
    index: index + 1,
    total: images.length,
  }));

  const onFullscreenOpened = (startIndex) => {
    dispatch(setFullscreenGalleryContent({ images: imageGalleryList, startIndex }));
    onClick(startIndex);
  };

  return (
    <>
      <Hide xs sm md>
        <ImageGrid images={imageGalleryList} layout={imageGridLayout} height={['220px', '480px']} onClick={onFullscreenOpened} />
      </Hide>
      <Hide lg xl>
        <ImageGallery images={imageGalleryList} onOpen={onFullscreenOpened} onImageChanged={onImageChanged} height={['220px', '480px']} />
      </Hide>
    </>
  );
};

LoadedImageGallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClick: PropTypes.func,
  onImageChanged: PropTypes.func,
};

const StandardImageGallery = memo(({ images, onClick = noop, onImageChanged = noop }) => {
  const isExclusiveOffer = useSelector(getExclusiveOffer);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isLoading = isExclusiveOffer ? useSelector(getIsLoadingExclusiveOffers) : useSelector(getIsLoading);

  return (
    <>
      <Hide xs sm md>
        <Box maxWidth="1600px" margin="0 auto">
          <ResultLoader isLoading={isLoading} skeletonResultCount={1} skeletonCardComponent={LoaderSkeletonCard}>
            <LoadedImageGallery images={images} onClick={onClick} onImageChanged={onImageChanged} />
          </ResultLoader>
        </Box>
      </Hide>
      <Hide lg xl>
        <Box maxWidth="100%">
          <ResultLoader isLoading={isLoading} skeletonResultCount={1} skeletonCardComponent={LoaderSkeletonCard}>
            <LoadedImageGallery images={images} onClick={onClick} onImageChanged={onImageChanged} />
          </ResultLoader>
        </Box>
      </Hide>
    </>
  );
});

StandardImageGallery.displayName = 'StandardImageGallery';

StandardImageGallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClick: PropTypes.func,
  onImageChanged: PropTypes.func,
};

export default StandardImageGallery;
